import { inject, Injectable } from '@angular/core';
import { type Observable, Subject } from 'rxjs';

import { NotificationService } from '@blaze-shared/platform-data-access';
import { type GeoPoint } from '@blaze-shared/shared-util';

/** How long a geolocation failure may take before it is assumed to be a remembered denial. */
const IMMEDIATE_FAILURE_MS = 100;

export interface UserLocation {
  latitude: number;
  location: GeoPoint;
  longitude: number;
}

/**
 * Reads the user's current location, yielding no rows when it cannot be determined.
 *
 * A failure is only shown to the user when it comes back immediately: the browser answers a
 * remembered denial at once, whereas a real attempt takes long enough that the user has already
 * seen the permission prompt and does not need telling twice.
 */
@Injectable({ providedIn: 'root' })
export class UserLocationActionService {
  private readonly notificationService = inject(NotificationService);

  getUserLocation(): Observable<UserLocation[]> {
    const location$$ = new Subject<UserLocation[]>();
    const requestedAt = Date.now();

    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }: GeolocationPosition) => {
        location$$.next([
          { latitude, location: { type: 'Point', coordinates: [longitude, latitude] }, longitude },
        ]);
      },
      (error: GeolocationPositionError) => {
        const message = locationErrorMessage(error);
        console.error(message, error);
        if (Date.now() - requestedAt < IMMEDIATE_FAILURE_MS) {
          this.notificationService.error(message);
        }
        location$$.next([]);
      },
    );
    return location$$;
  }
}

function locationErrorMessage(error: GeolocationPositionError): string {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return 'Location access denied. Enable permissions to continue.';
    case error.POSITION_UNAVAILABLE:
      return 'Location information unavailable. Please try again later.';
    case error.TIMEOUT:
      return 'Location request timed out. Try again.';
    default:
      return 'Unable to retrieve location due to an unknown error.';
  }
}
